import React, { useEffect, useState } from "react";
import { useFormik } from "formik";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";

import Dialog from "../shared/Dialog";
import DialogActions from "../DialogActions";
import Loader from "../shared/Loader";
import SpecialConditions from "../Interaction/SpecialConditions";
import { useAppContext } from "../../api/AppContext";
import useConfirmLeave from "../../hooks/useConfirmLeave";
import useModal from "../../hooks/useModal";
import { isCdpOnlyUser } from "../../lib/companyAccess";

const MATERIAL_CONDITION_TYPES = [
  "activeDays",
  "userStatePath",
  "survey",
  // "lmconsumed",
  "listOfUsers",
];

const EditMaterialConditionsModal = ({
  material,
  onClose,
  onConfirm,
  isDisabled,
  journeyId,
  days = 0,
}) => {
  const {
    userStatePaths,
    listofusers,
    surveys,
    // materials,
    fetchUserStatePaths,
    fetchListofusers,
    fetchAllSurveysForUser,
    user,
  } = useAppContext();

  const isCdpUser = isCdpOnlyUser(user);
  const [isReferenceDataLoading, setIsReferenceDataLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadReferenceData = async () => {
      setIsReferenceDataLoading(true);
      try {
        const fetches = [fetchUserStatePaths(), fetchListofusers()];
        if (!isCdpUser) {
          fetches.push(fetchAllSurveysForUser({ journeyId }));
        }
        await Promise.all(fetches);
      } finally {
        if (!cancelled) {
          setIsReferenceDataLoading(false);
        }
      }
    };

    loadReferenceData();

    return () => {
      cancelled = true;
    };
  }, [
    fetchAllSurveysForUser,
    fetchListofusers,
    fetchUserStatePaths,
    isCdpUser,
    journeyId,
  ]);

  const { handleConfirm, isLoading } = useModal({ onClose, onConfirm });

  const formik = useFormik({
    initialValues: {
      conditions: material?.conditions || [],
    },
    onSubmit: (values) => {
      handleConfirm({ conditions: values.conditions || [] });
    },
  });

  const { onClose: handleClose } = useConfirmLeave({
    formik,
    onClose,
  });

  return (
    <Dialog fullWidth open maxWidth="md" onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          Visibility conditions
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ overflowY: "visible" }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography color="textSecondary" variant="body2">
              {material?.name
                ? `Configure who can see "${material.name}" on the user dashboard. Leave empty to show to everyone.`
                : "Configure who can see this learning material on the user dashboard. Leave empty to show to everyone."}
            </Typography>
          </Grid>
          {journeyId &&
            (isReferenceDataLoading ? (
              <Grid item xs={12}>
                <Loader inline />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <SpecialConditions
                  days={days}
                  defaultType="activeDays"
                  formik={formik}
                  isDisabled={isDisabled}
                  journeyId={journeyId}
                  listofusers={listofusers || []}
                  surveys={surveys || []}
                  // trackedlm={(materials || []).filter((m) => m.allowTracking)}
                  types={MATERIAL_CONDITION_TYPES}
                  userStatePaths={userStatePaths}
                />
              </Grid>
            ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={
            isReferenceDataLoading ||
            !formik.dirty ||
            formik.isSubmitting ||
            isLoading
          }
          endIcon={
            (formik.isSubmitting || isLoading) && <CircularProgress size={25} />
          }
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Save
        </Button>
        <Button
          color="primary"
          disabled={formik.isSubmitting || isLoading}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditMaterialConditionsModal;
